import React, { useContext, useEffect, useRef, useState } from 'react';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import { TextButton, Text, ToggleSwitch, ButtonGroup, Headline, } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import ReactHtmlParser from 'react-html-parser';
import CloseIcon from './CloseIcon';
import { Container, BodyText, Heading, ToggleContainer, CloseButton, AlphaDialog, } from '../style/global.style';
import FeatureServiceContext from '../context/featureServiceContext';
import saveConsentToStorage from '../utils/saveConsentToStorage';
import { setupDialogListeners } from '../utils/openCloseModal';
import { getThemeName } from '../utils/alphaHelper';
const DialogWrapper = React.memo(({ children, isDialogOpen, setIsDialogOpen, twoClickStyle, longestButton, dialogRef, closeButtonCallback, }) => {
    var _a, _b;
    if (!process.env.IS_LEGACY && AlphaDialog) {
        return (React.createElement(AlphaDialog, { variant: "focus-large", open: isDialogOpen, padding: true, "data-test-id": "two-click", onClose: () => {
                setIsDialogOpen(false);
                closeButtonCallback === null || closeButtonCallback === void 0 ? void 0 : closeButtonCallback();
            }, twoClickStyle: twoClickStyle, onCloseButtonAriaLabel: "Close dialog" }, children));
    }
    return (React.createElement(Container, { "data-test-id": "two-click", longestButtonId: (_a = longestButton === null || longestButton === void 0 ? void 0 : longestButton.id) !== null && _a !== void 0 ? _a : '', longestButtonWidth: (_b = longestButton === null || longestButton === void 0 ? void 0 : longestButton.clientWidth) !== null && _b !== void 0 ? _b : 0, style: twoClickStyle, ref: dialogRef, id: "twoClickDialog" }, children));
});
const ConsentModal = ({ theme, headerText, bodyText, toggleText, showCloseButton, closeButtonCallback, showRedirectButton, redirectButtonText, redirectButtonURL, twoClickStyle, twoClickCancelCtaStyle, demo, }) => {
    var _a;
    const [content, setContent] = useState({
        theme,
        headerText,
        bodyText,
        toggleText,
        showCloseButton,
        showRedirectButton,
        redirectButtonText,
        redirectButtonURL,
    });
    const [toggle, setToggle] = useState(false);
    const [longestButton, setLongestButton] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(demo || false);
    const confirmButtonText = useI18n({
        defaultMessage: 'Confirm',
        id: 'fa.audi-dealer-search.two_click_confirm_button',
    });
    const context = useContext(FeatureServiceContext);
    const dialogRef = useRef(null);
    const getLongestButton = () => {
        var _a, _b;
        const confirmButton = document.getElementById('confirm-button');
        const redirectButton = document.getElementById('redirect-button');
        const confirmButtonWidth = (_a = confirmButton === null || confirmButton === void 0 ? void 0 : confirmButton.clientWidth) !== null && _a !== void 0 ? _a : 0;
        const redirectButtonWidth = (_b = redirectButton === null || redirectButton === void 0 ? void 0 : redirectButton.clientWidth) !== null && _b !== void 0 ? _b : 0;
        return confirmButtonWidth >= redirectButtonWidth ? confirmButton : redirectButton;
    };
    useEffect(() => {
        var _a, _b;
        if (context === null || context === void 0 ? void 0 : context.contentService) {
            const listener = () => {
                var _a;
                setContent((_a = context.contentService) === null || _a === void 0 ? void 0 : _a.getContent());
                setLongestButton(null);
            };
            (_b = (_a = context.contentService) === null || _a === void 0 ? void 0 : _a.onContentChange) === null || _b === void 0 ? void 0 : _b.call(_a, listener);
            return () => {
                var _a, _b;
                (_b = (_a = context.contentService) === null || _a === void 0 ? void 0 : _a.removeOnContentChange) === null || _b === void 0 ? void 0 : _b.call(_a, listener);
            };
        }
        return () => {
            console.log('No content service detected.');
        };
    }, [context.contentService]);
    useEffect(() => {
        setLongestButton(getLongestButton());
    }, [content]);
    useEffect(() => {
        setLongestButton(getLongestButton());
    }, []);
    useEffect(() => {
        setupDialogListeners('twoClickDialog');
    }, []);
    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (dialogRef.current && demo) {
                dialogRef.current.show();
            }
        }
    }, [dialogRef.current]);
    return (React.createElement(ThemeProvider, { theme: { name: getThemeName(content.theme) } },
        React.createElement(DialogWrapper, { isDialogOpen: isDialogOpen, setIsDialogOpen: setIsDialogOpen, twoClickStyle: twoClickStyle, longestButton: longestButton, dialogRef: dialogRef, closeButtonCallback: closeButtonCallback },
            content.showCloseButton && (React.createElement(CloseButton, { type: "button", className: "cancel-button", onClick: () => {
                    if (!process.env.IS_LEGACY) {
                        setIsDialogOpen(false);
                    }
                    closeButtonCallback === null || closeButtonCallback === void 0 ? void 0 : closeButtonCallback();
                }, style: twoClickCancelCtaStyle },
                React.createElement(CloseIcon, null))),
            React.createElement(Heading, null,
                React.createElement(Headline, { variant: { order: '2', type: 'headline', weight: 'normal' }, tag: "h2" }, content.headerText)),
            React.createElement(BodyText, null,
                React.createElement(Text, { tag: "span" }, ReactHtmlParser(content.bodyText))),
            React.createElement(ToggleContainer, null,
                React.createElement(ToggleSwitch, { onChange: () => setToggle(!toggle), id: "consent-toggle", checked: toggle, label: content.toggleText, value: `${toggle}`, name: "consent-toggle" })),
            React.createElement(ButtonGroup, { orientation: "horizontal", mode: "hug" },
                React.createElement(TextButton, Object.assign({ id: "confirm-button", className: "confirm-button", disabled: !toggle, variant: "primary" }, (!process.env.IS_LEGACY ? { size: 'xs' } : { size: 'sm' }), { onClick: () => {
                        if (!process.env.IS_LEGACY) {
                            setIsDialogOpen(false);
                        }
                        else if (dialogRef && dialogRef.current) {
                            dialogRef.current.close();
                        }
                        saveConsentToStorage(true);
                    } }), confirmButtonText),
                content.showRedirectButton && (React.createElement(TextButton, Object.assign({ id: "redirect-button", className: "redirect-button", variant: "secondary" }, (!process.env.IS_LEGACY ? { size: 'xs' } : { size: 'sm' }), { href: content.redirectButtonURL }), (_a = content.redirectButtonText) !== null && _a !== void 0 ? _a : ''))))));
};
export default ConsentModal;
